/**
 * Formatting utility functions for the application
 */
import dayjs from 'dayjs';

/**
 * Format a number to K/M/B format (e.g., 1000 -> 1K, 1000000 -> 1M, 1000000000 -> 1B)
 * @param value The number to format
 * @param round Whether to round the number to the nearest integer
 * @returns Formatted string in K/M/B format
 */
export const formatToKMB = (value: number, round: boolean = false): string => {
  if (!Number.isFinite(value)) {
    return value === Infinity ? '∞' : value === -Infinity ? '-∞' : '0';
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  const formatNumber = (num: number): string => {
    if (round) {
      return Math.round(num).toString();
    }

    return Number(num.toFixed(1)).toString();
  };

  if (absValue >= 1000000000) {
    return `${sign}${formatNumber(absValue / 1000000000)}B`;
  }

  if (absValue >= 1000000) {
    return `${sign}${formatNumber(absValue / 1000000)}M`;
  }

  if (absValue >= 1000) {
    return `${sign}${formatNumber(absValue / 1000)}K`;
  }

  return `${sign}${Math.round(absValue)}`;
};

/**
 * Formats a number or string as currency in Vietnamese format
 * @param amount - The amount to format
 * @returns Formatted currency string (e.g., 1.000.000)
 */
export const formatCurrency = (amount: number | string | undefined): string => {
  if (!amount && amount !== 0) return "--";

  // If it's a string, try to parse it to a number
  const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

  // Format using Intl
  return new Intl.NumberFormat("vi-VN", {
    style: "decimal",
    maximumFractionDigits: 0,
  }).format(numAmount);
};

/**
 * Removes formatting from a currency string
 * @param formattedValue - The formatted currency string (e.g., "1.000.000")
 * @returns The numeric value without formatting
 */
export const parseCurrency = (formattedValue: string): number => {
  // Remove all non-numeric characters except decimal separator
  const numericString = formattedValue.replace(/\./g, '').replace(/,/g, '.');
  return parseFloat(numericString);
};

/**
 * Converts a formatted currency string to a clean number
 * Handles formats like '434,343.00' (US) or '434.343,00' (Vietnamese) and converts to 434343
 * @param value - The value to convert (can be string, number, or any other type)
 * @returns A clean number without formatting, or undefined if conversion fails
 */
export const currencyToNumber = (value: any): number | undefined => {
  // Handle empty values
  if (value === undefined || value === null || value === '') {
    return undefined;
  }

  // If already a number, return it
  if (typeof value === 'number') {
    return value;
  }

  // If string, clean and convert
  if (typeof value === 'string') {
    // First, determine format (US or Vietnamese)
    // For US format (e.g., '434,343.00'):
    const usFormatClean = value.replace(/,/g, '').replace(/\..+$/, '');

    // For Vietnamese format (e.g., '434.343,00'):
    const vnFormatClean = value.replace(/\./g, '').replace(/,.+$/, '');

    // Try to parse as integer
    const numericValue = parseInt(usFormatClean, 10) || parseInt(vnFormatClean, 10);

    // Return the numeric value or undefined if parsing failed
    return isNaN(numericValue) ? undefined : numericValue;
  }

  return undefined;
};

/**
 * Formats a date range as day numbers, returning a single day if start and end are the same
 * @param dateStart - Start date (string, number, or Date)
 * @param dateEnd - End date (string, number, or Date)
 * @returns Formatted date string (e.g., "15" or "15 - 21")
 */
export const formatDateRange = (dateStart: string | number | Date, dateEnd: string | number | Date): string => {
  const startDate = dayjs(dateStart);
  const endDate = dayjs(dateEnd);
  if (startDate.isSame(endDate, 'day')) {
    return startDate.format('DD');
  }

  return [startDate.format('DD'), endDate.format('DD')].join(' - ');
};